import dagre from 'dagre';
import { Node, Edge, Position, MarkerType, EdgeMarker } from 'reactflow';
import { GraphLayoutDirection, NodeType } from 'src/constants/selfServe/executionViewConstants';
import { SUB_WORKFLOW_TASK_TYPE } from 'src/constants/CommonConstants';
import {
	Dependency,
	TaskExecutionData,
} from 'src/@types/selfServeTypes/taskExecutionData.interface';

const NODE_WIDTH = 200;
const NODE_HEIGHT = 50;
const RANK_SEP = 60;
const NODE_SEP = 40;
const GROUP_MARGIN_X = 20;
const GROUP_MARGIN_Y = 44;

export const SUB_WORKFLOW_EXECUTION_GROUP_NODE_TYPE = 'subWorkflowExecutionGroup';

const ArrowEnd: EdgeMarker = {
	type: MarkerType.ArrowClosed,
	color: '#000',
	width: 12,
	height: 12,
};

export interface ChildExecutionData {
	taskExecutions: TaskExecutionData[];
	dependencies: Dependency[];
	workflowName?: string;
}

export interface BuildExecutionGraphOptions {
	taskExecutions: TaskExecutionData[];
	dependencies: Dependency[];
	expandedExecutionIds: Set<string>;
	childExecutions: Map<string, ChildExecutionData>;
	direction: GraphLayoutDirection;
}

export interface NestedExecutionGraph {
	nodes: Node[];
	edges: Edge[];
}

const prefixed = (prefix: string, id: string) => (prefix ? `${prefix}${id}` : id);

export const isSubWorkflowTask = (task: {
	taskType?: string;
	childWorkflowId?: string;
	childWorkflowExecutionId?: string;
}): boolean =>
	task.taskType === SUB_WORKFLOW_TASK_TYPE &&
	Boolean(task.childWorkflowId) &&
	Boolean(task.childWorkflowExecutionId);

interface PlannedTask {
	nodeId: string;
	parentGroupId?: string;
	task: TaskExecutionData;
	isSubWorkflow: boolean;
	childExecutionId?: string;
}

interface PlannedTerminal {
	nodeId: string;
	parentGroupId?: string;
	type: NodeType;
}

interface PlannedGroup {
	groupId: string;
	parentGroupId?: string;
	workflowName: string;
	childWorkflowId: string;
	childExecutionId: string;
}

interface PlannedEdge {
	source: string;
	target: string;
}

function planGraph(options: BuildExecutionGraphOptions) {
	const { taskExecutions, dependencies, expandedExecutionIds, childExecutions } =
		options;

	const tasks: PlannedTask[] = [];
	const terminals: PlannedTerminal[] = [];
	const groups: PlannedGroup[] = [];
	const edges: PlannedEdge[] = [];
	const expandedTaskEntries = new Map<string, string[]>();
	const expandedTaskExits = new Map<string, string[]>();

	const walkTier = (
		tierTasks: TaskExecutionData[],
		tierDeps: Dependency[],
		idPrefix: string,
		parentGroupId: string | undefined,
		includeTerminals: boolean
	) => {
		if (includeTerminals) {
			[NodeType.START, NodeType.END].forEach((type) =>
				terminals.push({
					nodeId: prefixed(idPrefix, type),
					parentGroupId,
					type,
				})
			);
		}

		tierTasks.forEach((task) => {
			const expanded =
				isSubWorkflowTask(task) &&
				expandedExecutionIds.has(task.childWorkflowExecutionId as string) &&
				childExecutions.has(task.childWorkflowExecutionId as string);

			if (expanded) {
				const childExecutionId = task.childWorkflowExecutionId as string;
				const child = childExecutions.get(childExecutionId) as ChildExecutionData;
				const groupId = `group:${childExecutionId}`;
				const childPrefix = `${childExecutionId}:`;

				groups.push({
					groupId,
					parentGroupId,
					workflowName: child.workflowName || task.taskName || task.childWorkflowId || 'Sub-workflow',
					childWorkflowId: task.childWorkflowId as string,
					childExecutionId,
				});

				const isTerminal = (id: string) =>
					id === NodeType.START || id === NodeType.END;
				const hasIncoming = new Set(
					child.dependencies
						.filter((d) => !isTerminal(d.supporting))
						.map((d) => d.dependent)
				);
				const hasOutgoing = new Set(
					child.dependencies
						.filter((d) => !isTerminal(d.dependent))
						.map((d) => d.supporting)
				);
				const allChild = child.taskExecutions.map((ct) =>
					prefixed(childPrefix, ct.taskId)
				);
				const entries = child.taskExecutions
					.filter((ct) => !hasIncoming.has(ct.taskId))
					.map((ct) => prefixed(childPrefix, ct.taskId));
				const exits = child.taskExecutions
					.filter((ct) => !hasOutgoing.has(ct.taskId))
					.map((ct) => prefixed(childPrefix, ct.taskId));
				const taskNodeId = prefixed(idPrefix, task.taskId);

				expandedTaskEntries.set(taskNodeId, entries.length ? entries : allChild);
				expandedTaskExits.set(taskNodeId, exits.length ? exits : allChild);

				walkTier(
					child.taskExecutions,
					child.dependencies,
					childPrefix,
					groupId,
					false
				);

				return;
			}

			tasks.push({
				nodeId: prefixed(idPrefix, task.taskId),
				parentGroupId,
				task,
				isSubWorkflow: isSubWorkflowTask(task),
				childExecutionId: task.childWorkflowExecutionId,
			});
		});

		tierDeps.forEach((dep) => {
			edges.push({
				source: prefixed(idPrefix, dep.supporting),
				target: prefixed(idPrefix, dep.dependent),
			});
		});
	};

	walkTier(taskExecutions, dependencies, '', undefined, true);

	const realNodeIds = new Set([
		...tasks.map((t) => t.nodeId),
		...terminals.map((t) => t.nodeId),
	]);

	const resolveLeaves = (
		nodeId: string,
		kind: 'entry' | 'exit',
		seen: Set<string> = new Set()
	): string[] => {
		if (realNodeIds.has(nodeId)) return [nodeId];
		if (seen.has(nodeId)) return [];
		seen.add(nodeId);

		const map = kind === 'entry' ? expandedTaskEntries : expandedTaskExits;
		const next = map.get(nodeId);

		if (!next) return [];

		return next.flatMap((id) => resolveLeaves(id, kind, seen));
	};

	const remappedEdges: PlannedEdge[] = [];
	const seenEdge = new Set<string>();

	edges.forEach((edge) => {
		const sources = resolveLeaves(edge.source, 'exit');
		const targets = resolveLeaves(edge.target, 'entry');

		sources.forEach((source) =>
			targets.forEach((target) => {
				if (source === target) return;
				const key = `${source}->${target}`;

				if (seenEdge.has(key)) return;
				seenEdge.add(key);
				remappedEdges.push({ source, target });
			})
		);
	});

	return { tasks, terminals, groups, edges: remappedEdges };
}

export function buildNestedExecutionGraph(
	options: BuildExecutionGraphOptions
): NestedExecutionGraph {
	const { direction } = options;
	const isHorizontal = direction === GraphLayoutDirection.LeftToRight;
	const { tasks, terminals, groups, edges } = planGraph(options);

	const g = new dagre.graphlib.Graph({ compound: true });

	g.setDefaultEdgeLabel(() => ({}));
	g.setGraph({
		rankdir: direction,
		acyclicer: 'greedy',
		ranker: 'tight-tree',
		ranksep: RANK_SEP,
		nodesep: NODE_SEP,
	});

	groups.forEach((group) => {
		g.setNode(group.groupId, {
			clusterLabelPos: 'top',
			marginx: GROUP_MARGIN_X,
			marginy: GROUP_MARGIN_Y,
		});
	});

	tasks.forEach((t) => {
		g.setNode(t.nodeId, { width: NODE_WIDTH, height: NODE_HEIGHT });
	});
	terminals.forEach((t) => {
		g.setNode(t.nodeId, { width: NODE_WIDTH, height: NODE_HEIGHT });
	});

	groups.forEach((group) => {
		if (group.parentGroupId) g.setParent(group.groupId, group.parentGroupId);
	});
	tasks.forEach((t) => {
		if (t.parentGroupId) g.setParent(t.nodeId, t.parentGroupId);
	});
	terminals.forEach((t) => {
		if (t.parentGroupId) g.setParent(t.nodeId, t.parentGroupId);
	});

	const validEndpoints = new Set([
		...tasks.map((t) => t.nodeId),
		...terminals.map((t) => t.nodeId),
	]);
	const isValidEndpoint = (id: string) => validEndpoints.has(id);

	edges.forEach((edge) => {
		if (
			isValidEndpoint(edge.source) &&
			isValidEndpoint(edge.target) &&
			edge.source !== edge.target
		) {
			g.setEdge(edge.source, edge.target);
		}
	});

	dagre.layout(g);

	const groupTopLeft = new Map<string, { x: number; y: number }>();

	groups.forEach((group) => {
		const pos = g.node(group.groupId);

		groupTopLeft.set(group.groupId, {
			x: (pos?.x ?? 0) - (pos?.width ?? 0) / 2,
			y: (pos?.y ?? 0) - (pos?.height ?? 0) / 2,
		});
	});

	const relativePosition = (
		absX: number,
		absY: number,
		parentGroupId?: string
	) => {
		if (!parentGroupId) return { x: absX, y: absY };
		const origin = groupTopLeft.get(parentGroupId) ?? { x: 0, y: 0 };

		return { x: absX - origin.x, y: absY - origin.y };
	};

	const groupNodes: Node[] = groups.map((group) => {
		const pos = g.node(group.groupId);
		const absX = (pos?.x ?? 0) - (pos?.width ?? 0) / 2;
		const absY = (pos?.y ?? 0) - (pos?.height ?? 0) / 2;

		return {
			id: group.groupId,
			type: SUB_WORKFLOW_EXECUTION_GROUP_NODE_TYPE,
			position: relativePosition(absX, absY, group.parentGroupId),
			data: {
				workflowName: group.workflowName,
				childWorkflowId: group.childWorkflowId,
				childExecutionId: group.childExecutionId,
			},
			style: {
				width: pos?.width ?? NODE_WIDTH,
				height: pos?.height ?? NODE_HEIGHT,
			},
			...(group.parentGroupId
				? { parentNode: group.parentGroupId, extent: 'parent' as const }
				: {}),
		};
	});

	const terminalNodes: Node[] = terminals.map((t) => {
		const pos = g.node(t.nodeId);
		const absX = (pos?.x ?? 0) - NODE_WIDTH / 2;
		const absY = (pos?.y ?? 0) - NODE_HEIGHT / 2;

		return {
			id: t.nodeId,
			type: 'terminalNode',
			position: relativePosition(absX, absY, t.parentGroupId),
			data: { type: t.type, alignment: direction },
			targetPosition: isHorizontal ? Position.Left : Position.Top,
			sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
			...(t.parentGroupId
				? { parentNode: t.parentGroupId, extent: 'parent' as const }
				: {}),
		};
	});

	const taskNodes: Node[] = tasks.map((t) => {
		const pos = g.node(t.nodeId);
		const absX = (pos?.x ?? 0) - NODE_WIDTH / 2;
		const absY = (pos?.y ?? 0) - NODE_HEIGHT / 2;

		return {
			id: t.nodeId,
			type: 'taskNode',
			position: relativePosition(absX, absY, t.parentGroupId),
			data: {
				...t.task,
				alignment: direction,
				isSubWorkflow: t.isSubWorkflow,
				childExecutionId: t.childExecutionId,
			},
			targetPosition: isHorizontal ? Position.Left : Position.Top,
			sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
			...(t.parentGroupId
				? { parentNode: t.parentGroupId, extent: 'parent' as const }
				: {}),
		};
	});

	const resultEdges: Edge[] = edges
		.filter(
			(e) =>
				isValidEndpoint(e.source) &&
				isValidEndpoint(e.target) &&
				e.source !== e.target
		)
		.map((e) => ({
			id: `${e.source}-${e.target}`,
			source: e.source,
			target: e.target,
			type: 'default',
			style: { stroke: '#000' },
			markerEnd: ArrowEnd,
		}));

	return {
		nodes: [...groupNodes, ...terminalNodes, ...taskNodes],
		edges: resultEdges,
	};
}
